// src/pages/Admin/Talleres/CambiosRevisiones/index.jsx
import React, { useEffect, useState } from "react";
import {
  Card, Table, Tag, Button, Space, Modal, Form, Input, Typography,
  Alert, Empty, Tooltip, Badge
} from "antd";
import {
  CheckCircleOutlined, CloseCircleOutlined, EyeOutlined,
  UserOutlined, CalendarOutlined,
  InfoCircleOutlined, HistoryOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/es";
import Talleres from "../../../../../service/Talleres";
import Recintos from "../../../../../service/Recintos";
import useAuthContext from "../../../../../hooks/useAuthContext";
import { useWithNotify, useNotify } from "../../../../../providers/NotificationProvider";
import { App } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { FiCheck, FiFileText, FiX } from "react-icons/fi";
import { claseBtnPrimario, claseBtnSecundario } from "../../../../Ui/Admin/PiezasAdmin";
import { CabeceraModal, ChipCabecera, Chip, Diff, etiquetaCampo, textoValor, claseBtnPeligro } from "../piezasTalleres";

dayjs.extend(relativeTime);
dayjs.locale("es");

const { Title, Text } = Typography;
const { TextArea } = Input;

const pesosCLP = (v) => ((v || v === 0) ? `$${Number(v).toLocaleString('es-CL')}` : null);

const PRECIOS = {
  valorParticular: 'Particular',
  valorVecino: 'Vecino',
  valorFuncionarios: 'Funcionarios',
  valorAdultoMayor: 'Adulto mayor',
  matricula: 'Matrícula',
};

// Precios de un taller, plan por plan.
const ModalidadPagoVisual = ({ modalidades, esGratuito }) => {
  if (esGratuito) return <Chip tono="azul">Taller gratuito · no se paga para inscribirse</Chip>;

  const parse = (data) => {
    if (typeof data === 'string') {
      try { return JSON.parse(data); } catch { return []; }
    }
    return Array.isArray(data) ? data : (data ? [data] : []);
  };
  const planes = parse(modalidades);
  if (!planes.length) return <span className="text-sm text-gray-400">Sin precios configurados</span>;

  return (
    <div className="space-y-2">
      {planes.map((plan, idx) => (
        <div key={idx} className="rounded-xl bg-white border border-gray-100 px-3 py-2.5">
          <p className="text-xs font-bold text-gray-900 capitalize m-0 mb-1.5">Plan {plan.plan}</p>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            {Object.entries(PRECIOS).filter(([k]) => pesosCLP(plan[k])).map(([k, label]) => (
              <div key={k} className="flex justify-between gap-2 text-xs">
                <span className="text-gray-500">{label}</span>
                <span className="font-semibold text-gray-900 tabular-nums">{pesosCLP(plan[k])}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// Fila de dato para los talleres nuevos.
const FilaDato = ({ label, children }) => (
  <div className="grid grid-cols-1 sm:grid-cols-[200px_1fr] gap-1 sm:gap-4 py-3 border-b border-gray-100 last:border-0">
    <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500 m-0 sm:pt-0.5">{label}</p>
    <div className="text-sm text-gray-900 min-w-0 break-words">{children}</div>
  </div>
);

export default function CambiosRevisiones() {
  const [loading, setLoading] = useState(false);
  const [cambiosPendientes, setCambiosPendientes] = useState([]);
  const { user } = useAuthContext();
  
  const withNotify = useWithNotify();
  const notify = useNotify();
  const { modal } = App.useApp();

  const [recintos, setRecintos] = useState([]);
  const [loadingRecintos, setLoadingRecintos] = useState(false);

  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedCambio, setSelectedCambio] = useState(null);

  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [rejectForm] = Form.useForm();
  const [actionLoading, setActionLoading] = useState(false);

  // Igual que la ficha de usuario: en pantallas angostas el detalle ocupa todo el ancho.
  const angosto = typeof window !== 'undefined' && window.innerWidth < 900;

  const loadRecintos = async () => {
    try {
      setLoadingRecintos(true);
      const res = await Recintos.List();

      setRecintos(res.data || res || []);
    } catch (error) {
      console.error('Error cargando recintos:', error);
      notify.error(error.message || "No se pudieron cargar los recintos");
    } finally {
      setLoadingRecintos(false);
    }
  };

  const loadCambiosPendientes = async () => {
    try {
      setLoading(true);
      const res = await Talleres.ListarCambiosPendientes();

      setCambiosPendientes(res || []);
    } catch (error) {
      notify.error(error.message || "No se pudieron cargar los cambios pendientes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRecintos();
    loadCambiosPendientes();
  }, []);

  const getNombreRecinto = (recintoId) => {
    if (!recintoId) return 'Sin recinto';
    
    if (typeof recintoId === 'object' && recintoId.nombre) {
      return recintoId.nombre;
    }
    
    const recinto = recintos.find(r => r._id === recintoId || r.id === recintoId);
    return recinto?.nombre || recinto?.name || recintoId;
  };

  const renderHorarios = (horarios) => {
    if (!horarios || horarios.length === 0) {
      return <span className="text-sm text-gray-400">Sin horarios</span>;
    }
    const lista = Array.isArray(horarios) ? horarios : [];

    return (
      <div className="space-y-1.5">
        {lista.map((horario, idx) => {
          if (typeof horario === 'string') {
            return <p key={idx} className="text-xs text-gray-400 m-0">Horario {horario}</p>;
          }
          const bloques = horario.bloques?.length ? horario.bloques : [{ dias: horario.dias || [], inicio: horario.inicio, fin: horario.fin }];
          const disp = horario.cupos
            ? Math.max(0, horario.cupos - (horario.cuposOcupados || 0) - (horario.cuposReservados || 0))
            : null;
          return (
            <div key={idx} className="flex items-center justify-between gap-3 px-3 py-2 rounded-xl bg-white border border-gray-100">
              <div className="space-y-1 min-w-0">
                {bloques.map((b, i) => (
                  <div key={i} className="flex items-center gap-1.5 flex-wrap">
                    {(b.dias || []).map(d => <Chip key={d} tono="azul" className="capitalize">{d.slice(0, 3)}</Chip>)}
                    {b.inicio && <span className="text-xs font-semibold text-gray-700 tabular-nums">{b.inicio}–{b.fin}</span>}
                  </div>
                ))}
              </div>
              {horario.cuposIlimitados
                ? <span className="text-xs text-gray-500 flex-shrink-0">Sin límite</span>
                : disp !== null && <span className="text-xs text-gray-500 flex-shrink-0 tabular-nums">{disp}/{horario.cupos} libres</span>}
            </div>
          );
        })}
      </div>
    );
  };

  const handleViewDetail = (cambio) => {
    setSelectedCambio(cambio);
    setDetailModalVisible(true);
  };

  const handleAprobar = async (cambio) => {
    modal.confirm({
      title: "Confirmar aprobación",
      icon: <CheckCircleOutlined style={{ color: '#0047bb' }} />,
      content: (
        <div>
          <p>¿Estás seguro de aprobar este cambio?</p>
          <p className="text-gray-500">
            {cambio.tipoOperacion === 'creacion' 
              ? 'El taller será creado y publicado automáticamente.'
              : 'Los cambios se aplicarán al taller existente.'}
          </p>
        </div>
      ),
      okText: "Aprobar",
      okType: "primary",
      okButtonProps: { className: 'bg-primary' },
      cancelText: "Cancelar",
      onOk: async () => {
        try {
          await withNotify(
            Talleres.AprobarCambio(cambio._id),
            {
              loading: "Aprobando cambio...",
              success: "Cambio aprobado exitosamente",
              error: "No se pudo aprobar el cambio"
            }
          );
          loadCambiosPendientes();
          setDetailModalVisible(false);
        } catch (error) {
          console.error('Error aprobando:', error);
        }
      }
    });
  };

  const handleOpenReject = (cambio) => {
    setSelectedCambio(cambio);
    setRejectModalVisible(true);
  };

  const handleRechazar = async (values) => {
    try {
      setActionLoading(true);
      await withNotify(
        Talleres.RechazarCambio(selectedCambio._id, values.motivoRechazo),
        {
          loading: "Rechazando cambio...",
          success: "Cambio rechazado correctamente",
          error: "No se pudo rechazar el cambio"
        }
      );
      
      rejectForm.resetFields();
      setRejectModalVisible(false);
      setDetailModalVisible(false);
      loadCambiosPendientes();
    } catch (error) {
      console.error('Error rechazando:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const formatFecha = (fecha) => {
    if (!fecha) return 'No definido';
    return dayjs(fecha).format("DD/MM/YYYY HH:mm");
  };

  // Un campo modificado, como "Hoy / Propuesto". Los precios se muestran junto con
  // si el taller es gratuito, así que `esGratuito` no tiene fila propia.
  const renderCambioDiff = (campo, valorAnterior, valorNuevo, cambioCompleto) => {
    if (campo === 'modalidadPago' || campo === 'ModalidadPago') {
      const cambioGratuito = cambioCompleto?.camposModificados?.find(c => c.campo === 'esGratuito');
      const vacio = (v) => !v || (Array.isArray(v) && v.length === 0);
      const gratuitoAntes = cambioGratuito ? cambioGratuito.valorAnterior : vacio(valorAnterior);
      const gratuitoAhora = cambioGratuito ? cambioGratuito.valorNuevo : vacio(valorNuevo);
      return (
        <Diff
          titulo="Precios"
          antes={<ModalidadPagoVisual modalidades={valorAnterior} esGratuito={gratuitoAntes} />}
          propuesto={<ModalidadPagoVisual modalidades={valorNuevo} esGratuito={gratuitoAhora} />}
        />
      );
    }

    if (campo === 'esGratuito') return null;

    if (campo === 'horarios') {
      return (
        <Diff
          titulo="Horarios"
          antes={renderHorarios(valorAnterior?.horariosDetalle || valorAnterior || [])}
          propuesto={renderHorarios(valorNuevo?.horariosDetalle || valorNuevo || [])}
        />
      );
    }

    if (campo === 'recinto') {
      return <Diff titulo="Recinto" antes={getNombreRecinto(valorAnterior)} propuesto={getNombreRecinto(valorNuevo)} />;
    }

    if (['inscripcionInicio', 'inscripcionFin', 'actividadesInicio', 'actividadesFin'].includes(campo)) {
      return <Diff titulo={etiquetaCampo(campo)} antes={formatFecha(valorAnterior)} propuesto={formatFecha(valorNuevo)} />;
    }

    if (typeof valorNuevo !== 'object' || valorNuevo === null) {
      return <Diff titulo={etiquetaCampo(campo)} antes={textoValor(valorAnterior)} propuesto={textoValor(valorNuevo)} />;
    }

    // Objetos que no tienen una vista propia: se muestran tal cual, legibles.
    const crudo = (v) => (
      <pre className="text-xs font-normal overflow-auto max-h-40 m-0 whitespace-pre-wrap">
        {v != null ? JSON.stringify(v, null, 2) : 'Sin valor'}
      </pre>
    );
    return <Diff titulo={etiquetaCampo(campo)} antes={crudo(valorAnterior)} propuesto={crudo(valorNuevo)} />;
  };

  const nombreDe = (cambio) => (cambio?.tipoOperacion === 'creacion'
    ? cambio?.datosNuevos?.nombre
    : cambio?.tallerId?.nombre) || 'Taller sin nombre';
  const recintoDe = (cambio) => getNombreRecinto(cambio?.tipoOperacion === 'creacion'
    ? cambio?.datosNuevos?.recinto
    : cambio?.tallerId?.recinto);

  const columns = [
    {
      title: "Tipo",
      dataIndex: "tipoOperacion",
      key: "tipoOperacion",
      width: 120,
      render: (tipo) => (
        <Tag color={tipo === 'creacion' ? 'blue' : 'orange'}>
          {tipo === 'creacion' ? 'Nuevo taller' : 'Modificación'}
        </Tag>
      ),
    },
    {
      title: "Taller",
      key: "taller",
      render: (_, record) => {
        const nombreTaller = record.tipoOperacion === 'creacion'
          ? record.datosNuevos?.nombre
          : record.tallerId?.nombre || 'Taller sin nombre';
        
        return (
          <div>
            <Text strong className="block">{nombreTaller}</Text>
            {record.tipoOperacion === 'modificacion' && (
              <Text type="secondary" className="text-xs">
                {record.camposModificados?.length || 0} campos modificados
              </Text>
            )}
          </div>
        );
      },
    },
    {
      title: "Recinto",
      key: "recinto",
      width: 180,
      render: (_, record) => {
        if (record.tipoOperacion === 'creacion') {
          const recintoId = record.datosNuevos?.recinto;
          const nombreRecinto = getNombreRecinto(recintoId);
          
          return (
            <Tag color="cyan" className="text-xs">
              {loadingRecintos ? 'Cargando...' : nombreRecinto}
            </Tag>
          );
        }
        
        const recintoId = record.tallerId?.recinto;
        const nombreRecinto = getNombreRecinto(recintoId);
        
        return (
          <Tag color="cyan" className="text-xs">
            {loadingRecintos ? 'Cargando...' : nombreRecinto}
          </Tag>
        );
      },
    },
    {
      title: "Solicitado por",
      key: "solicitante",
      render: (_, record) => (
        <div>
          <div className="flex items-center gap-1">
            <UserOutlined className="text-gray-400" />
            <Text>{record.solicitadoPor?.nombre}</Text>
          </div>
          <Tag size="small" color="purple">
            {record.solicitadoPor?.rol}
          </Tag>
        </div>
      ),
    },
    {
      title: "Fecha solicitud",
      dataIndex: "solicitadoEn",
      key: "fecha",
      width: 180,
      render: (fecha) => (
        <div>
          <div className="flex items-center gap-1">
            <CalendarOutlined className="text-gray-400" />
            <Text className="text-xs">{dayjs(fecha).format("DD/MM/YYYY HH:mm")}</Text>
          </div>
          <Text type="secondary" className="text-xs">
            {dayjs(fecha).fromNow()}
          </Text>
        </div>
      ),
    },
    {
      title: "Acciones",
      key: "acciones",
      width: 280,
      render: (_, record) => (
        <Space wrap>
          <Tooltip title="Ver detalles">
            <Button
              icon={<EyeOutlined />}
              size="small"
              onClick={() => handleViewDetail(record)}
            >
              Ver detalles
            </Button>
          </Tooltip>
          <Tooltip title="Aprobar cambio">
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              size="small"
              onClick={() => handleAprobar(record)}
            >
              Aprobar
            </Button>
          </Tooltip>
          <Tooltip title="Rechazar cambio">
            <Button
              danger
              icon={<CloseCircleOutlined />}
              size="small"
              onClick={() => handleOpenReject(record)}
            >
              Rechazar
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="p-4 md:p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <Title level={3} className="!mb-0">Cambios y Revisiones</Title>
          <Text type="secondary">
            Revisa y aprueba los cambios pendientes de los talleres
          </Text>
        </div>
        <Badge count={cambiosPendientes.length} showZero color="#0047bb">
          <Button
            icon={<HistoryOutlined />}
            onClick={loadCambiosPendientes}
            loading={loading}
          >
            Actualizar
          </Button>
        </Badge>
      </div>

      <Alert
        message="Sistema de aprobaciones activo"
        description="Los jefes de recinto necesitan aprobación de admin/coordinador para crear o modificar talleres. Los cambios aprobados se reflejan automáticamente."
        type="info"
        icon={<InfoCircleOutlined />}
        showIcon
        closable
        className="mb-4"
      />

      <Card>
        <Table
          loading={loading || loadingRecintos}
          dataSource={cambiosPendientes}
          columns={columns}
          rowKey="_id"
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No hay cambios pendientes de revisión"
              />
            )
          }}
          pagination={{
            pageSize: 10,
            showTotal: (total) => `Total: ${total} cambios pendientes`,
          }}
        />
      </Card>

      {/* Modal de detalle */}
      <Modal
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        closable={false}
        title={null}
        width={angosto ? '100%' : 900}
        destroyOnHidden
        styles={{ content: { padding: 0, overflow: 'hidden', borderRadius: 24 }, body: { padding: 0, background: '#f4f6f9' } }}
      >
        {selectedCambio && (() => {
          const esCreacion = selectedCambio.tipoOperacion === 'creacion';
          const campos = (selectedCambio.camposModificados || []).filter(c => c.campo !== 'esGratuito');
          const d = selectedCambio.datosNuevos || {};
          return (
            <>
              <CabeceraModal
                icon={<FiFileText size={24} />}
                titulo={nombreDe(selectedCambio)}
                subtitulo={recintoDe(selectedCambio)}
                onClose={() => setDetailModalVisible(false)}
                chips={(
                  <>
                    <ChipCabecera>{esCreacion ? 'Taller nuevo' : `Modificación · ${campos.length} campo${campos.length !== 1 ? 's' : ''}`}</ChipCabecera>
                    <ChipCabecera activo={false}>Esperando revisión</ChipCabecera>
                  </>
                )}
                datos={[
                  {
                    label: 'Lo pidió',
                    value: selectedCambio.solicitadoPor?.nombre || '—',
                    sub: [selectedCambio.solicitadoPor?.rol, selectedCambio.solicitadoPor?.email].filter(Boolean).join(' · '),
                  },
                  {
                    label: 'Pedido',
                    value: dayjs(selectedCambio.solicitadoEn).fromNow(),
                    sub: dayjs(selectedCambio.solicitadoEn).format('DD/MM/YYYY HH:mm'),
                  },
                ]}
              />

              <div className="p-5 sm:p-6 space-y-3">
                {esCreacion ? (
                  <>
                    <Alert type="info" showIcon
                      message="Si lo apruebas, el taller se crea y se publica"
                      description="Cada persona que se inscriba tendrá su propia suscripción desde el día que pague." />
                    <section className="rounded-3xl bg-white border border-gray-100 px-5 py-2">
                      <FilaDato label="Nombre">{d.nombre}</FilaDato>
                      <FilaDato label="Recinto">{getNombreRecinto(d.recinto)}</FilaDato>
                      <FilaDato label="Tipo de actividad">{d.tipoActividad || '—'}</FilaDato>
                      <FilaDato label="Descripción">{d.descripcion || '—'}</FilaDato>
                      <FilaDato label="Edades">{d.edadMin ?? 0} a {d.edadMax || 'sin límite'} años</FilaDato>
                      <FilaDato label="Inscripciones">{formatFecha(d.inscripcionInicio)} → {formatFecha(d.inscripcionFin)}</FilaDato>
                      <FilaDato label="Cupo al darse de baja">{d.liberarCuposPorBaja ? 'Se libera' : 'No se libera'}</FilaDato>
                      <FilaDato label="Horarios">{renderHorarios(d.horariosDetalle || d.horarios)}</FilaDato>
                      <FilaDato label="Precios"><ModalidadPagoVisual modalidades={d.modalidadPago} esGratuito={d.esGratuito} /></FilaDato>
                    </section>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-gray-600 m-0">
                      Si lo apruebas, estos cambios se aplican al taller de inmediato. Lo que hoy tiene el taller queda a la izquierda; lo propuesto, a la derecha.
                    </p>
                    {campos.length === 0
                      ? <p className="text-sm text-gray-400 m-0">El cambio no trae campos modificados.</p>
                      : campos.map((c, idx) => (
                        <React.Fragment key={idx}>
                          {renderCambioDiff(c.campo, c.valorAnterior, c.valorNuevo, selectedCambio)}
                        </React.Fragment>
                      ))}
                  </>
                )}

                <div className="flex flex-wrap justify-end gap-2 pt-3">
                  <button type="button" onClick={() => setDetailModalVisible(false)} className={claseBtnSecundario}>Volver</button>
                  <button type="button" className={claseBtnPeligro}
                    onClick={() => { setDetailModalVisible(false); handleOpenReject(selectedCambio); }}>
                    <FiX size={15} /> Rechazar
                  </button>
                  <button type="button" onClick={() => handleAprobar(selectedCambio)} disabled={actionLoading} className={claseBtnPrimario}>
                    {actionLoading ? <LoadingOutlined /> : <FiCheck size={15} />} {esCreacion ? 'Aprobar y crear taller' : 'Aprobar cambios'}
                  </button>
                </div>
              </div>
            </>
          );
        })()}
      </Modal>

      {/* Modal de rechazo */}
      <Modal
        open={rejectModalVisible}
        onCancel={() => { setRejectModalVisible(false); rejectForm.resetFields(); }}
        width={520}
        destroyOnHidden
        title={<>Rechazar <span className="hl-amarillo">cambio</span></>}
        footer={
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => { setRejectModalVisible(false); rejectForm.resetFields(); }} className={claseBtnSecundario}>Volver</button>
            <button type="button" onClick={() => rejectForm.submit()} disabled={actionLoading} className={claseBtnPeligro}>
              {actionLoading ? <LoadingOutlined /> : <FiX size={15} />} Rechazar cambio
            </button>
          </div>
        }
      >
        {selectedCambio && (
          <p className="text-sm text-gray-600 mb-4">
            <strong>{nombreDe(selectedCambio)}</strong> · pedido por {selectedCambio.solicitadoPor?.nombre || 'alguien del equipo'}.
            El taller queda como está y quien lo pidió ve el motivo para corregirlo.
          </p>
        )}
        <Form form={rejectForm} layout="vertical" onFinish={handleRechazar} requiredMark={false}>
          <Form.Item
            name="motivoRechazo"
            label="Motivo (lo verá quien lo pidió)"
            rules={[
              { required: true, message: 'Escribe el motivo' },
              { min: 10, message: 'Al menos 10 caracteres, para que se entienda qué corregir' },
            ]}
            style={{ marginBottom: 0 }}
          >
            <TextArea rows={4} placeholder="Ej: faltan los horarios del sábado; revísalos y vuelve a enviarlo." />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
